<template>
  <el-form
    ref="areaForm"
    v-loading="isLoading"
    element-loading-text="載入專案與區域資料中..."
    element-loading-spinner="el-icon-loading"
    element-loading-background="rgba(255, 255, 255, 0.85)"
    :model="form"
    :validate-on-rule-change="false"
    :hide-required-asterisk="true"
    label-position="top"
    class="project-area-form"
  >
    <!-- 專案選擇 -->
    <el-row>
      <el-col :span="24">
        <el-form-item
          prop="project"
          label="專案"
        >
          <el-select
            v-model="form.project"
            filterable
            style="width: 100%;"
          >
            <el-option
              v-for="project in projectList"
              :key="project._id"
              :value="project._id"
              :label="project.project_id || project.name || project._id"
            ></el-option>
          </el-select>
        </el-form-item>
      </el-col>
    </el-row>

    <!-- 區域過濾工具列 -->
    <div class="search-toolbar flex-row align-center justify-between mb-16">
      <el-input
        v-model="searchInput"
        placeholder="過濾清單（輸入關鍵字搜尋）..."
        prefix-icon="el-icon-search"
        clearable
        size="small"
        class="search-input"
        @input="areaFilter"
        @clear="areaFilter"
      >
        <template slot="append" v-if="searchUseReg">
          <el-tag size="mini" type="success" effect="dark">RegExp</el-tag>
        </template>
      </el-input>
      <div class="regex-toggle-wrapper flex-row align-center">
        <span class="regex-label">正則表達式</span>
        <el-tooltip content="支援正則表達式篩選" placement="top">
          <el-switch
            v-model="searchUseReg"
            active-color="#13ce66"
            inactive-color="#dcdfe6"
            @change="areaFilter"
          ></el-switch>
        </el-tooltip>
      </div>
    </div>

    <!-- 區域配置與拖拉排序區域 -->
    <el-form-item
      prop="area"
      class="area-form-item"
    >
      <div slot="label" class="flex-row justify-between align-center label-header">
        <span class="form-item-title">
          <strong>區域配置</strong>
          <span class="hint-text">（可直接拖曳 Tag 調整順序，點擊 ✕ 移除）</span>
        </span>
        <el-tag size="small" type="info">
          已選 {{ form.area.length }} / 未選 {{ candidateCount }}
        </el-tag>
      </div>
      <el-drag-select
        v-model="form.area"
        multiple
        filterable
        allow-create
        default-first-option
        placeholder="點擊下拉選單選取區域，或輸入名稱 Enter 新增"
        class="full-width-drag-select"
      >
        <el-option
          v-for="area in areaList"
          :key="area._id"
          :label="area.name"
          :value="area._id"
        />
      </el-drag-select>
    </el-form-item>

    <!-- 底部操作列與提示 -->
    <div class="form-footer flex-row justify-between align-center">
      <div class="footer-tip">
        <i class="el-icon-info"></i>
        <span>調整順序請點擊「更新排序」；新增或刪減區域請點擊「更新專案」</span>
      </div>
      <div class="button-group flex-row align-center">
        <el-button
          type="warning"
          icon="el-icon-sort"
          size="medium"
          :loading="isLoading"
          :disabled="!form.area.length || isLoading"
          @click="updateSelectSort"
        >
          更新排序
        </el-button>
        <el-button
          type="primary"
          icon="el-icon-check"
          size="medium"
          :loading="isLoading"
          :disabled="!selectedProject || isLoading"
          @click="submitForm"
        >
          更新專案
        </el-button>
      </div>
    </div>
  </el-form>
</template>

<script>
export default {
  props: {
    projectId: {
      type: String,
      default: '',
      required: false,
    },
  },
  data() {
    return {
      isLoading: true,
      isInitialized: false,
      form: {
        project: '',
        area: [],
      },
      allAreaList: [],
      areaList: [],
      searchInput: '',
      searchUseReg: false,
    };
  },
  computed: {
    selectedProject() {
      const selectedProjectId = this.form.project;
      const selectedProject = this.projectList.find(ele => ele._id === selectedProjectId);
      return selectedProject;
    },
    areaIdList() {
      return this.allAreaList.map(area => area._id);
    },
    projectList() {
      return this.$store.getters['project/projectList'] || [];
    },
    candidateCount() {
      return this.allAreaList.length;
    },
  },
  watch: {
    projectId(newValue, oldValue) {
      if (newValue !== oldValue) {
        this.form.project = newValue;
      }
    },
    'form.project'(newValue, oldValue) {
      if (newValue !== oldValue) {
        this.form.area = [];
        this.setProjectArea();
        if (this.isInitialized && newValue) {
          this.searchInput = '';
          this.areaFilter();
        }
      }
    },
  },
  async mounted() {
    if (this.projectId) {
      this.form.project = this.projectId;
    } else {
      const selectedProj = this.$store.getters['project/selectedProject'] || this.$store.state.project.selectedProject;
      this.form.project = selectedProj ? selectedProj._id : '';
    }
    this.isLoading = true;
    try {
      await Promise.all([
        this.initProjectList(),
        this.getAreaList(),
      ]);
      this.setProjectArea();
      this.areaFilter();
    } catch (e) {
      this.$notifyError(e);
    } finally {
      this.isLoading = false;
      this.isInitialized = true;
    }
  },
  methods: {
    async initProjectList() {
      if (!this.projectList || !this.projectList.length) {
        await this.$store.dispatch('project/getProjectList').catch(() => null);
      }
    },
    setProjectArea() {
      if (this.selectedProject && Array.isArray(this.selectedProject.area)) {
        this.form.area = [...this.selectedProject.area];
      } else {
        this.form.area = [];
      }
    },
    getAreaList(isUpdate = false) {
      return this.$store
        .dispatch('data/getAreaList', isUpdate)
        .then(res => {
          this.allAreaList = res || [];
          return this.allAreaList;
        })
        .catch(this.$notifyError);
    },
    areaFilter() {
      const currentSelectedIds = new Set(
        (this.form.area || []).map(id => (typeof id === 'object' && id !== null ? id._id : id)),
      );

      let pool = [...this.allAreaList];
      const query = this.searchInput ? this.searchInput.trim() : '';
      if (query) {
        if (this.searchUseReg) {
          try {
            const reg = new RegExp(query, 'i');
            pool = pool.filter(ele => reg.test(ele.name) || currentSelectedIds.has(ele._id));
          } catch (e) {
            // regex fallback
          }
        } else {
          const lowerQuery = query.toLowerCase();
          pool = pool.filter(ele => (ele.name && ele.name.toLowerCase().includes(lowerQuery)) || currentSelectedIds.has(ele._id));
        }
      }

      // 保底聯集：確保已選項目都存在於 areaList，避免顯示 ObjectId
      const map = new Map();
      pool.forEach(item => map.set(item._id, item));
      currentSelectedIds.forEach(id => {
        if (!map.has(id)) {
          const found = this.allAreaList.find(i => i._id === id);
          if (found) {
            map.set(id, found);
          } else {
            map.set(id, { _id: id, name: id });
          }
        }
      });

      this.areaList = Array.from(map.values());
    },
    async updateSelectSort() {
      let message = '此操作將改變區域排序';
      const response = await this.$confirmCreate(message);
      if (response === 'confirm') {
        this.isLoading = true;
        let area = this.form.area;
        if (!area.length) {
          this.$notifyError('區域列表不能為空');
          this.isLoading = false;
          return;
        }

        try {
          await this.removeAllAreaFromProject();
          await this.addAreaToProject(area);

          this.$notifySuccess('修改成功');
          await this.getAreaList(true);
          await this.$store.dispatch('project/getProjectList', true);
          this.setProjectArea();
          this.areaFilter();
        } catch (error) {
          console.log(error);
          this.$notifyError(error);
        } finally {
          this.isLoading = false;
        }
      }
    },
    async removeAllAreaFromProject() {
      return this.$axios({
        category: 'admin',
        method: 'delete',
        apiName: `project/${this.form.project}/allarea`,
        data: {
          area: [],
        },
      }).catch(this.$notifyError);
    },
    submitForm() {
      this.$refs.areaForm.validate(async valid => {
        if (valid) {
          this.updateProjectArea();
        } else {
          return false;
        }
      });
    },
    async addAreaToProject(areaIdList) {
      if (!areaIdList.length) return;
      return this.$axios({
        category: 'admin',
        method: 'post',
        apiName: `project/${this.form.project}/area`,
        data: {
          area: areaIdList,
        },
      }).catch(this.$notifyError);
    },
    async removeAreaFromProject(areaIdList) {
      if (!areaIdList.length) return;
      return this.$axios({
        category: 'admin',
        method: 'delete',
        apiName: `project/${this.form.project}/area`,
        data: {
          area: areaIdList,
        },
      }).catch(this.$notifyError);
    },
    async createProjectNewArea(areaIdList) {
      if (!areaIdList.length) return;
      return this.$axios({
        category: 'admin',
        method: 'post',
        apiName: 'area',
        data: {
          project: this.form.project,
          area: areaIdList,
        },
      }).catch(this.$notifyError);
    },
    async updateProjectArea() {
      const currentProjectAreas = (this.selectedProject && this.selectedProject.area) || [];
      const knownIds = new Set(this.allAreaList.map(a => a._id));
      const isExisting = id => knownIds.has(id);

      const removedArea = currentProjectAreas.filter(area => {
        return !this.form.area.includes(area);
      });
      const newAddedArea = this.form.area.filter(area => {
        return !isExisting(area) && !currentProjectAreas.includes(area);
      });
      const pushedArea = this.form.area.filter(area => {
        return !currentProjectAreas.includes(area) && isExisting(area);
      });

      if (!newAddedArea.length && !removedArea.length && !pushedArea.length) {
        return this.$notifyInfo('沒有改動');
      }

      let message = '此操作將';
      if (newAddedArea.length) {
        message += `新增 ${newAddedArea.length} 個區域並加至該專案\n`;
      }
      if (removedArea.length) {
        message += `從該專案移除 ${removedArea.length} 個區域\n`;
      }
      if (pushedArea.length) {
        message += `更新 ${pushedArea.length} 個區域至該專案\n`;
      }
      const response = await this.$confirmCreate(message);
      if (response === 'confirm') {
        this.isLoading = true;
        Promise.all([
          this.createProjectNewArea(newAddedArea),
          this.removeAreaFromProject(removedArea),
          this.addAreaToProject(pushedArea),
        ])
          .then(async () => {
            this.$notifySuccess('新增成功');
            await this.getAreaList(true);
            await this.$store.dispatch('project/getProjectList', true);
            this.setProjectArea();
            this.areaFilter();
          })
          .catch(error => {
            console.log(error);
            this.$notifyError(error);
          })
          .finally(() => {
            this.isLoading = false;
          });
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.project-area-form {
  min-width: 620px;
  max-width: 760px;
  min-height: 280px;

  ::v-deep .el-form-item {
    margin-bottom: 18px;
  }

  ::v-deep .el-select,
  ::v-deep .full-width-drag-select {
    width: 100%;
  }

  ::v-deep .drag-select {
    display: block;
    position: relative;
    width: 100%;

    .el-select__tags {
      position: relative !important;
      top: auto !important;
      left: auto !important;
      transform: none !important;
      width: 100% !important;
      max-width: 100% !important;
      min-height: 42px !important;
      padding: 5px 36px 5px 10px !important;
      box-sizing: border-box !important;
      display: flex !important;
      flex-wrap: wrap !important;
      align-items: center !important;
      z-index: 1;

      > span {
        display: flex;
        flex-wrap: wrap;
        align-items: center;
      }
    }

    .el-tag {
      margin: 3px 4px 3px 0;
      cursor: grab;
      user-select: none;
      &:active {
        cursor: grabbing;
      }
    }

    .el-select__input {
      margin-left: 6px;
      height: 28px;
      line-height: 28px;
    }

    .el-input {
      position: absolute !important;
      top: 0 !important;
      left: 0 !important;
      width: 100% !important;
      height: 100% !important;
      pointer-events: none;
      z-index: 0;

      .el-input__inner {
        height: 100% !important;
        min-height: 42px !important;
      }

      .el-input__suffix {
        pointer-events: auto;
        display: flex;
        align-items: center;
        right: 8px;
      }
    }
  }
}

.search-toolbar {
  background: #f8fafc;
  padding: 10px 14px;
  border-radius: 6px;
  border: 1px solid #ebeef5;

  .search-input {
    max-width: 380px;
  }

  .regex-toggle-wrapper {
    display: flex;
    align-items: center;
  }

  .regex-label {
    font-size: 13px;
    color: #606266;
    margin-right: 8px;
  }
}

.label-header {
  width: 100%;
  margin-bottom: 4px;
}

.form-item-title {
  font-size: 14px;
  color: #303133;
}

.hint-text {
  font-size: 12px;
  font-weight: normal;
  color: #909399;
  margin-left: 6px;
}

.form-footer {
  margin-top: 24px;
  padding-top: 16px;
  border-top: 1px solid #ebeef5;
}

.footer-tip {
  font-size: 12px;
  color: #909399;
  display: flex;
  align-items: center;
  i {
    margin-right: 5px;
    color: #e6a23c;
    font-size: 14px;
  }
}

.button-group {
  display: flex;
  align-items: center;
  .el-button + .el-button {
    margin-left: 12px;
  }
}
</style>
